import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API, timeout: 30000 });

http.interceptors.request.use((config) => {
  const t = localStorage.getItem("medihub_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

http.interceptors.response.use(
  (r) => r,
  (err) => {
    const p = window.location.pathname;
    const isPublic = p.startsWith("/login") || p.startsWith("/invite") || p.startsWith("/p/");
    const isAuthCall = (err.config?.url || "").startsWith("/auth/");
    if (err.response?.status === 401 && !isPublic && !isAuthCall) {
      localStorage.removeItem("medihub_token");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function fmtErr(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const api = {
  // auth
  login: (email, password) => http.post("/auth/login", { email, password }).then(r => r.data),
  devLogin: () => http.post("/auth/dev-login").then(r => r.data),
  twofaSetup: (pre_token) => http.post("/auth/2fa/setup", { pre_token }).then(r => r.data),
  twofaVerify: (pre_token, code) => http.post("/auth/2fa/verify", { pre_token, code }).then(r => r.data),
  me: () => http.get("/auth/me").then(r => r.data),
  logout: () => http.post("/auth/logout").then(r => r.data),
  getInvite: (token) => http.get(`/auth/invite/${token}`).then(r => r.data),
  acceptInvite: (token, data) => http.post(`/auth/invite/${token}/accept`, data).then(r => r.data),
  // team (admin)
  teamList: () => http.get("/team").then(r => r.data),
  teamInvite: (data) => http.post("/team/invite", data).then(r => r.data),
  teamPatch: (id, data) => http.patch(`/team/${id}`, data).then(r => r.data),
  teamReset2fa: (id) => http.post(`/team/${id}/reset-2fa`).then(r => r.data),
  inviteResend: (id) => http.post(`/team/invites/${id}/resend`).then(r => r.data),
  inviteRevoke: (id) => http.delete(`/team/invites/${id}`).then(r => r.data),
  // contacts
  listContacts: (params) => http.get("/contacts", { params }).then(r => r.data),
  contactFilterOptions: () => http.get("/contacts/filter-options").then(r => r.data),
  contactStats: () => http.get("/contacts/stats").then(r => r.data),
  getContact: (id) => http.get(`/contacts/${id}`).then(r => r.data),
  contactTimeline: (id) => http.get(`/contacts/${id}/timeline`).then(r => r.data),
  createContact: (data) => http.post("/contacts", data).then(r => r.data),
  updateContact: (id, data) => http.patch(`/contacts/${id}`, data).then(r => r.data),
  deleteContact: (id) => http.delete(`/contacts/${id}`).then(r => r.data),
  bulkAction: (data) => http.post("/contacts/bulk-action", data).then(r => r.data),
  // tags
  listTags: () => http.get("/tags").then(r => r.data),
  createTag: (name) => http.post("/tags", { name }).then(r => r.data),
  deleteTag: (name) => http.delete(`/tags/${encodeURIComponent(name)}`).then(r => r.data),
  // country backfill
  countryBackfillStart: () => http.post("/country/backfill/start").then(r => r.data),
  countryBackfillLatest: () => http.get("/country/backfill/latest").then(r => r.data),
  bulkPaste: (data) => http.post("/contacts/bulk-paste", data).then(r => r.data),
  importPreview: (file) => { const fd = new FormData(); fd.append("file", file); return http.post("/contacts/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then(r => r.data); },
  importApply: (id) => http.post(`/contacts/import/${id}/apply`).then(r => r.data),
  importStatus: (id) => http.get(`/contacts/import/${id}`).then(r => r.data),
  importLatest: () => http.get("/contacts/import/latest").then(r => r.data),
  // lists
  listLists: () => http.get("/lists").then(r => r.data),
  createList: (data) => http.post("/lists", data).then(r => r.data),
  getList: (id) => http.get(`/lists/${id}`).then(r => r.data),
  listAdd: (id, ids) => http.post(`/lists/${id}/add`, { contact_ids: ids }).then(r => r.data),
  listAddByFilter: (id, params) => http.post(`/lists/${id}/add-by-filter`, null, { params }).then(r => r.data),
  listRemove: (id, ids) => http.post(`/lists/${id}/remove`, { contact_ids: ids }).then(r => r.data),
  updateList: (id, data) => http.patch(`/lists/${id}`, data).then(r => r.data),
  deleteList: (id) => http.delete(`/lists/${id}`).then(r => r.data),
  // list folders
  listListGroups: () => http.get("/list-groups").then(r => r.data),
  createListGroup: (name) => http.post("/list-groups", { name }).then(r => r.data),
  renameListGroup: (id, name) => http.patch(`/list-groups/${id}`, { name }).then(r => r.data),
  deleteListGroup: (id) => http.delete(`/list-groups/${id}`).then(r => r.data),
  batchAllContacts: (data) => http.post("/lists/batch-all", data).then(r => r.data),
  // segments
  listSegments: () => http.get("/segments").then(r => r.data),
  createSegment: (data) => http.post("/segments", data).then(r => r.data),
  previewSegment: (data) => http.post("/segments/preview", data).then(r => r.data),
  deleteSegment: (id) => http.delete(`/segments/${id}`).then(r => r.data),
  // templates
  listTemplates: () => http.get("/templates").then(r => r.data),
  createTemplate: (data) => http.post("/templates", data).then(r => r.data),
  getTemplate: (id) => http.get(`/templates/${id}`).then(r => r.data),
  updateTemplate: (id, data) => http.patch(`/templates/${id}`, data).then(r => r.data),
  deleteTemplate: (id) => http.delete(`/templates/${id}`).then(r => r.data),
  // campaigns
  listCampaigns: () => http.get("/campaigns").then(r => r.data),
  listCampaignGroups: () => http.get("/campaign-groups").then(r => r.data),
  folderReport: (id) => http.get(`/campaign-groups/${id}/report`).then(r => r.data),
  folderReportPdf: (id) => http.get(`/campaign-groups/${id}/report.pdf`, { responseType: "blob" }).then(r => r.data),
  moveStatus: () => http.get("/move-detector/status").then(r => r.data),
  moveRun: (limit) => http.post("/move-detector/run", { limit }).then(r => r.data),
  moveProposals: () => http.get("/move-detector/proposals").then(r => r.data),
  moveApply: (check_id) => http.post("/move-detector/apply", { check_id }).then(r => r.data),
  moveDismiss: (check_id) => http.post("/move-detector/dismiss", { check_id }).then(r => r.data),
  createCampaignGroup: (name) => http.post("/campaign-groups", { name }).then(r => r.data),
  renameCampaignGroup: (id, name) => http.patch(`/campaign-groups/${id}`, { name }).then(r => r.data),
  deleteCampaignGroup: (id) => http.delete(`/campaign-groups/${id}`).then(r => r.data),
  nextWave: (id) => http.post(`/campaign-groups/${id}/next-wave`).then(r => r.data),
  scannerStats: (id) => http.get(`/campaigns/${id}/scanner-stats`).then(r => r.data),
  createCampaign: (data) => http.post("/campaigns", data).then(r => r.data),
  getCampaign: (id) => http.get(`/campaigns/${id}`).then(r => r.data),
  updateCampaign: (id, data) => http.patch(`/campaigns/${id}`, data).then(r => r.data),
  audienceCount: (data) => http.post("/campaigns/audience-count", data).then(r => r.data),
  deleteCampaign: (id) => http.delete(`/campaigns/${id}`).then(r => r.data),
  testSend: (id, email) => http.post(`/campaigns/${id}/test-send`, { to_email: email }).then(r => r.data),
  testSendSeeds: (id) => http.post(`/campaigns/${id}/test-send-seeds`).then(r => r.data),
  sendCampaign: (id) => http.post(`/campaigns/${id}/send`).then(r => r.data),
  stopCampaign: (id) => http.post(`/campaigns/${id}/stop`).then(r => r.data),
  sendApproval: (id) => http.post(`/campaigns/${id}/send-approval`).then(r => r.data),
  campaignRecipients: (id) => http.get(`/campaigns/${id}/recipients`).then(r => r.data),
  campaignHealth: (id) => http.get(`/campaigns/${id}/health`).then(r => r.data),
  compareCampaigns: (ids) => http.get(`/campaigns/compare`, { params: { ids } }).then(r => r.data),
  campaignReport: (id) => http.get(`/campaigns/${id}/report.pdf`, { responseType: "blob" }).then(r => r.data),
  duplicateCampaign: (id) => http.post(`/campaigns/${id}/duplicate`).then(r => r.data),
  publicPrefs: (cid) => http.get(`/public/preferences/${cid}`).then(r => r.data),
  savePublicPrefs: (cid, data) => http.post(`/public/preferences/${cid}`, data).then(r => r.data),
  abStatus: (id) => http.get(`/campaigns/${id}/ab-status`).then(r => r.data),
  renderPreview: (data) => http.post("/campaigns/render-preview", data).then(r => r.data),
  // list health + hygiene
  listHealth: () => http.get("/list-health").then(r => r.data),
  suppressStale: (days = 180) => http.post(`/contacts/suppress-stale?days=${days}`).then(r => r.data),
  cleanContactData: () => http.post("/admin/clean-contact-data", null, { timeout: 300000 }).then(r => r.data),
  linkedinReview: (id, action) => http.post(`/contacts/${id}/linkedin-review`, { action }).then(r => r.data),
  // attachments
  listAttachments: (id) => http.get(`/campaigns/${id}/attachments`).then(r => r.data),
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post(`/campaigns/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  deleteAttachment: (cid, aid) => http.delete(`/campaigns/${cid}/attachments/${aid}`).then(r => r.data),
  // ai
  aiSubject: (body, tone="professional", n=3, extra={}) => http.post("/ai/suggest-subject", { body, tone, n, ...extra }).then(r => r.data),
  senderHealth: (days=30) => http.get(`/health/sender?days=${days}`).then(r => r.data),
  domainHealth: () => http.get("/health/domain").then(r => r.data),
  // sequences
  listSequences: () => http.get("/sequences").then(r => r.data),
  createSequence: (data) => http.post("/sequences", data).then(r => r.data),
  updateSequence: (id, data) => http.patch(`/sequences/${id}`, data).then(r => r.data),
  deleteSequence: (id) => http.delete(`/sequences/${id}`).then(r => r.data),
  sequenceRequestApproval: (id) => http.post(`/sequences/${id}/request-approval`).then(r => r.data),
  sequenceActivate: (id) => http.post(`/sequences/${id}/activate`).then(r => r.data),
  sequencePause: (id) => http.post(`/sequences/${id}/pause`).then(r => r.data),
  followupCounts: (cid) => http.get(`/campaigns/${cid}/follow-up-counts`).then(r => r.data),
  createFollowup: (cid, data) => http.post(`/campaigns/${cid}/follow-up`, data).then(r => r.data),
  aiRewrite: (text, instruction) => http.post("/ai/rewrite", { text, instruction }).then(r => r.data),
  aiDraft: (goal, tone, audience) => http.post("/ai/draft", { goal, tone, audience }).then(r => r.data),
  aiDesign: (goal, tone, audience) => http.post("/ai/design", { goal, tone, audience }).then(r => r.data),
  uploadImage: (file) => { const fd = new FormData(); fd.append("file", file); return http.post("/images", fd).then(r => r.data); },
  listImages: () => http.get("/images").then(r => r.data),
  deleteImage: (id) => http.delete(`/images/${id}`).then(r => r.data),
  exportDesign: () => http.get("/admin/export-design").then(r => r.data),
  importDesign: (file) => { const fd = new FormData(); fd.append("file", file); return http.post("/admin/import-design", fd).then(r => r.data); },
  // engagement + preflight
  recomputeEngagement: () => http.post("/contacts/recompute-engagement").then(r => r.data),
  scoreBreakdown: (id) => http.get(`/contacts/${id}/score-breakdown`).then(r => r.data),
  preflight: (id) => http.get(`/campaigns/${id}/preflight`).then(r => r.data),
  // companies
  globalSearch: (q) => http.get("/search", { params: { q } }).then(r => r.data),
  mergeSuggestions: () => http.get("/companies/merge-suggestions").then(r => r.data),
  autotagStart: (data) => http.post("/companies/autotag/start", data).then(r => r.data),
  autotagLatest: () => http.get("/companies/autotag/latest").then(r => r.data),
  autotagReviews: () => http.get("/companies/autotag/reviews").then(r => r.data),
  autotagReviewAction: (rid, data) => http.post(`/companies/autotag/reviews/${rid}`, data).then(r => r.data),
  apiKeys: () => http.get("/integrations/keys").then(r => r.data),
  createApiKey: (data) => http.post("/integrations/keys", data).then(r => r.data),
  revokeApiKey: (id) => http.delete(`/integrations/keys/${id}`).then(r => r.data),
  mergeCompanies: (keep_id, remove_id) => http.post("/companies/merge", { keep_id, remove_id }).then(r => r.data),
  listCompanies: (params) => http.get("/companies", { params }).then(r => r.data),
  createCompany: (data) => http.post("/companies", data).then(r => r.data),
  updateCompany: (id, data) => http.patch(`/companies/${id}`, data).then(r => r.data),
  deleteCompany: (id, deleteContacts = false) => http.delete(`/companies/${id}`, { params: { delete_contacts: deleteContacts } }).then(r => r.data),
  syncCompanies: () => http.post("/companies/sync-from-contacts").then(r => r.data),
  applyCompanyTags: () => http.post("/companies/apply-all-tags").then(r => r.data),
  // gated publications
  listPublications: () => http.get("/publications").then(r => r.data),
  createPublication: (file, fields) => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ""));
    return http.post("/publications", fd).then(r => r.data);
  },
  updatePublication: (id, data) => http.patch(`/publications/${id}`, data).then(r => r.data),
  deletePublication: (id) => http.delete(`/publications/${id}`).then(r => r.data),
  listPubRequests: (status) => http.get("/publication-requests", { params: status ? { status } : {} }).then(r => r.data),
  approvePubRequest: (id) => http.post(`/publication-requests/${id}/approve`).then(r => r.data),
  denyPubRequest: (id) => http.post(`/publication-requests/${id}/deny`).then(r => r.data),
  publicPublication: (id, cid) => http.get(`/public/publications/${id}`, { params: cid ? { cid } : {} }).then(r => r.data),
  submitPubRequest: (id, data) => http.post(`/public/publications/${id}/request`, data).then(r => r.data),
  // hunter prospecting
  hunterAccount: () => http.get("/hunter/account").then(r => r.data),
  hunterSearch: (params) => http.get("/hunter/domain-search", { params, timeout: 60000 }).then(r => r.data),
  hunterImport: (data) => http.post("/hunter/import", data, { timeout: 120000 }).then(r => r.data),
  hunterCacheLookup: (query) => http.get("/hunter/cache-lookup", { params: { query } }).then(r => r.data),
  hunterCache: (domain) => http.get("/hunter/cache", { params: { domain } }).then(r => r.data),
  hunterSearches: () => http.get("/hunter/searches").then(r => r.data),
  hunterGlobalSearch: (data) => http.post("/hunter/global-search", data, { timeout: 180000 }).then(r => r.data),
  hunterRevealImport: (data) => http.post("/hunter/reveal-import", data, { timeout: 180000 }).then(r => r.data),
  apolloSearch: (data) => http.post("/apollo/search", data, { timeout: 60000 }).then(r => r.data),
  apolloTitleSearch: (data) => http.post("/apollo/title-search", data, { timeout: 60000 }).then(r => r.data),
  apolloRevealImport: (data) => http.post("/apollo/reveal-import", data, { timeout: 180000 }).then(r => r.data),
  apolloAccount: () => http.get("/apollo/account").then(r => r.data),
  fitscanAnalyze: (data) => http.post("/fitscan/analyze", data, { timeout: 150000 }).then(r => r.data),
  fitscanRate: (data) => http.post("/fitscan/rate", data, { timeout: 150000 }).then(r => r.data),
  fitscanGet: (domain) => http.get(`/fitscan/${domain}`).then(r => r.data),
  hunterFindEmail: (data) => http.post("/hunter/find-email", data, { timeout: 60000 }).then(r => r.data),
  siteScanPeople: (data) => http.post("/site/scan-people", data, { timeout: 60000 }).then(r => r.data),
  siteScanStatus: (jobId) => http.get(`/site/scan-people/${jobId}`).then(r => r.data),
  siteImportPerson: (data) => http.post("/site/import-person", data).then(r => r.data),
  // cleanup
  cleanupPreview: () => http.get("/cleanup/preview", { timeout: 120000 }).then(r => r.data),
  cleanupDelete: (ids) => http.post("/cleanup/delete", { contact_ids: ids }, { timeout: 120000 }).then(r => r.data),
  cleanupAiScan: (maxTitles) => http.post("/cleanup/ai-scan", maxTitles ? { max_titles: maxTitles } : {}).then(r => r.data),
  cleanupAiStatus: (id) => http.get(`/cleanup/ai-scan/${id}`).then(r => r.data),
  // enrichment & verification (Hunter)
  repliesParse: (text) => http.post("/replies/parse", { text }, { timeout: 90000 }).then(r => r.data),
  repliesParseFile: async (file) => {
    let data_b64 = null;
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let s = "";
      for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
      data_b64 = btoa(s);
    } catch (_) {
      data_b64 = await new Promise((resolve) => {
        const r = new FileReader();
        r.onerror = () => resolve(null);
        r.onload = () => resolve(String(r.result).split(",")[1] || "");
        r.readAsDataURL(file);
      });
    }
    if (data_b64) {
      return http.post("/replies/parse-file-b64", { filename: file.name || "reply.msg", data_b64 }, { timeout: 90000 }).then(r => r.data);
    }
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "reply.msg");
      const r = await http.post("/replies/parse-file", fd, { timeout: 90000 });
      return r.data;
    } catch (e) {
      if (e?.response) throw e;
      throw new Error("Your computer wouldn't let the browser read this file. If it lives in OneDrive, right-click it and pick 'Always keep on this device' — or save the email to a local folder first, or just paste the reply text instead.");
    }
  },
  repliesApply: (payload) => http.post("/replies/apply", payload).then(r => r.data),
  enrichLatest: () => http.get("/enrich/latest").then(r => r.data),
  enrichStart: () => http.post("/enrich/start").then(r => r.data),
  enrichContact: (id) => http.post(`/enrich/contact/${id}`, {}, { timeout: 60000 }).then(r => r.data),
  verifyLatest: () => http.get("/verify/latest").then(r => r.data),
  verifyStart: (campaignId) => http.post("/verify/start", { campaign_id: campaignId || null }).then(r => r.data),
  verifyContact: (id, force = false) => http.post(`/verify/contact/${id}?force=${force}`, {}, { timeout: 60000 }).then(r => r.data),
  markDeliverable: (id) => http.post(`/contacts/${id}/mark-deliverable`).then(r => r.data),
  warmupPreview: (body) => http.post("/warmup/preview", body, { timeout: 120000 }).then(r => r.data),
  warmupCreate: (body) => http.post("/warmup/create", body).then(r => r.data),
  lbCensus: () => http.get("/listbuilder/census", { timeout: 60000 }).then(r => r.data),
  lbStart: (body) => http.post("/listbuilder/start", body, { timeout: 120000 }).then(r => r.data),
  lbJob: (id) => http.get(`/listbuilder/job/${id}`, { timeout: 60000 }).then(r => r.data),
  lbJobRetry: (id) => http.post(`/listbuilder/job/${id}/retry`).then(r => r.data),
  lbCreate: (body) => http.post("/listbuilder/create", body).then(r => r.data),
  rescueScan: () => http.get("/rescue/scan", { timeout: 60000 }).then(r => r.data),
  rescueFind: (body) => http.post("/rescue/find", body, { timeout: 120000 }).then(r => r.data),
  rescueVerifyPending: (body) => http.post("/rescue/verify-pending", body, { timeout: 90000 }).then(r => r.data),
  rescueApply: (body) => http.post("/rescue/apply", body).then(r => r.data),
  rescueDismiss: (body) => http.post("/rescue/dismiss", body).then(r => r.data),
  campaignVerification: (cid) => http.get(`/campaigns/${cid}/verification-summary`).then(r => r.data),
  // misc
  overview: () => http.get("/dashboard/overview").then(r => r.data),
  cmoDashboard: (days) => http.get("/dashboard/cmo", { params: { days } }).then(r => r.data),
  hotLeads: (status = "new") => http.get("/hot-leads", { params: { status } }).then(r => r.data),
  hotLeadContacted: (id) => http.post(`/hot-leads/${id}/contacted`).then(r => r.data),
  hotLeadDismiss: (id) => http.post(`/hot-leads/${id}/dismiss`).then(r => r.data),
  hotLeadDraft: (id, instructions) => http.post(`/hot-leads/${id}/draft`, { instructions }, { timeout: 90000 }).then(r => r.data),
  hotLeadSend: (id, payload) => http.post(`/hot-leads/${id}/send`, payload).then(r => r.data),
  suppressions: () => http.get("/suppressions").then(r => r.data),
  removeSuppression: (email) => http.delete(`/suppressions/${encodeURIComponent(email)}`).then(r => r.data),
  settings: () => http.get("/settings").then(r => r.data),
  updateSettings: (data) => http.patch("/settings", data).then(r => r.data),
  guide: () => http.get("/user-guide").then(r => r.data),
};
